import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useGainPop } from "../lib/useGainPop";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LogOut, Trophy, Users, ShoppingBag, Award, Layers, User, Shield, Mail, Users2, ShieldAlert, AlertTriangle } from "lucide-react";
import { EmailAuthModal } from "./EmailAuthModal";
import { toast } from "sonner";
import { ASSETS } from "../lib/api";
import { HandshakeIcon } from "./HandshakeIcon";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { visibleInterval } from "../lib/visibleInterval";

function CurrencyBadge({ icon, label, value, color, testId, popOnGain }) {
  const pop = useGainPop(popOnGain ? value : 0);
  return (
    <div
      key={pop?.key}
      data-testid={testId}
      className={`relative shrink-0 flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 rounded-lg bg-[#1C0D40]/70 border ${pop ? "cur-pop" : ""}`}
      style={{ borderColor: color + "55", "--c": color }}
    >
      {typeof icon === "string" ? <img src={icon} alt={label} className="w-6 h-6" /> : icon}
      <span className="font-mono text-sm font-bold" style={{ color, textShadow: `0 0 8px ${color}99` }}>
        {value.toLocaleString()}
      </span>
      {pop && (
        <span data-testid={`${testId}-gain`} className="cur-gain absolute left-1/2 top-full mt-0.5 font-num text-base whitespace-nowrap pointer-events-none z-50"
              style={{ color, textShadow: `0 0 8px ${color}, 1px 1px 0 #000` }}>
          +{pop.diff.toLocaleString()}
        </span>
      )}
    </div>
  );
}

export default function TopMenu() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);
  const [myTeam, setMyTeam] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [loc.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (!e.target.closest('[data-testid="top-menu"]')) setMenuOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [menuOpen]);

  useEffect(() => {
    if (!user || !user.account_name) return;
    let cancelled = false;
    const fetchUnread = () => {
      api.get("/inbox/unread")
        .then((r) => { if (!cancelled) setUnread(r.data?.unread || 0); })
        .catch(() => {});
    };
    const fetchTeam = () => {
      api.get("/tag-teams/mine")
        .then((r) => { if (!cancelled) setMyTeam(r.data?.team || null); })
        .catch(() => {});
    };
    fetchUnread(); fetchTeam();
    const stopPoll = visibleInterval(() => { fetchUnread(); fetchTeam(); }, 20000);
    const onFocus = () => { fetchUnread(); fetchTeam(); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("inbox-refresh", fetchUnread);
    window.addEventListener("tag-team-refresh", fetchTeam);
    return () => {
      cancelled = true;
      stopPoll();
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("inbox-refresh", fetchUnread);
      window.removeEventListener("tag-team-refresh", fetchTeam);
    };
  }, [user?.user_id, user?.account_name]);

  if (!user || !user.account_name) return null;

  const NavBtn = ({ to, label, icon: Icon, testId, badge = 0 }) => {
    const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
    return (
      <button
        data-testid={testId}
        onClick={() => nav(to)}
        className={`relative px-3 py-2 rounded-lg font-heading justify-center md:justify-start uppercase text-xs tracking-widest flex items-center gap-2 transition-all
          ${active ? "bg-[#FF007F]/20 text-white border border-[#FF007F]" : "text-[#A89BCC] hover:text-white border border-transparent hover:border-[#00F0FF]/60"}`}
      >
        <Icon size={14} />
        {label}
        {badge > 0 && (
          <span
            data-testid={`${testId}-badge`}
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center font-mono font-bold text-[10px]"
            style={{
              background: "#FF007F",
              color: "white",
              boxShadow: "0 0 10px #FF007FAA",
              animation: "pulse-neon 1.5s ease-in-out infinite",
            }}
          >
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <header
      data-testid="top-menu"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A051A]/85 border-b border-[#FF007F]/30"
    >
      <div className="relative max-w-7xl mx-auto px-2 md:px-4 py-1.5 md:py-2 flex flex-col gap-2">
        <nav className={`${menuOpen ? "grid" : "hidden"} grid-cols-2 absolute top-full inset-x-0 p-2 gap-1 bg-[#0A051A]/95 backdrop-blur-xl border-b border-[#FF007F]/30 md:static md:p-0 md:bg-transparent md:backdrop-blur-none md:border-0 md:flex items-center md:gap-1 flex-wrap justify-between w-full`} data-testid="main-menu">
          <NavBtn to="/roster" label="Roster" icon={Users} testId="nav-roster" />
          <NavBtn to="/pvp" label="PvP Shows" icon={Trophy} testId="nav-pvp" />
          {myTeam && (
            <NavBtn to={`/tag-team/${myTeam.team_id}`} label="Tag Team" icon={Users2} testId="nav-tag-team" />
          )}
          <NavBtn to="/promotion" label="Promotion" icon={Shield} testId="nav-promotion" />
          <NavBtn to="/cards" label="Items" icon={Layers} testId="nav-cards" />
          <NavBtn to="/shop" label="Shop" icon={ShoppingBag} testId="nav-shop" />
          <NavBtn to="/profile" label="Profile" icon={User} testId="nav-profile" />
          <NavBtn to="/leaderboard" label="Highscore" icon={Trophy} testId="nav-leaderboard" />
          <NavBtn to="/achievements" label="Achievements" icon={Award} testId="nav-achievements" badge={user?.achievements_claimable_unseen || 0} />
          {user?.role && (
            <NavBtn to="/admin" label={user.role === "admin" ? "Admin" : "Mod"} icon={ShieldAlert} testId="nav-admin" />
          )}
          <button data-testid="mobile-logout-btn" onClick={logout}
                  className="md:hidden px-3 py-2 rounded-lg font-heading uppercase text-xs tracking-widest flex items-center justify-center gap-2 text-[#A89BCC] border border-transparent hover:text-[#FF007F] hover:border-[#FF007F]/60">
            <LogOut size={14} /> Logout
          </button>
        </nav>

        <div className="flex items-center gap-1.5 md:gap-2 flex-nowrap md:flex-wrap" data-testid="bottom-menu">
        <button data-testid="mobile-menu-toggle" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu"
                className={`md:hidden shrink-0 p-2 rounded-lg border transition-colors ${menuOpen ? "border-[#FF007F] text-white bg-[#FF007F]/20" : "border-[#FF007F]/40 text-[#A89BCC]"}`}>
          {menuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
        <div
          data-testid="logo"
          className="hidden md:flex font-heading font-black text-xl cursor-pointer items-center gap-2"
          onClick={() => nav("/roster")}
        >
          <img
            src="/logos/Logo_RingKings_BottomMainMenu.webp"
            alt="Ring Kings"
            data-testid="bottom-menu-logo"
            className="h-7 w-auto transition-transform duration-200 hover:scale-105"
            style={{ filter: "drop-shadow(0 0 6px #FF007FAA)" }}
          />
        </div>

        <div className="ml-auto min-w-0 flex items-center gap-1.5 md:gap-2 flex-nowrap md:flex-wrap">
          {user.is_guest && (
            <button data-testid="guest-save-btn" onClick={() => setSaveOpen(true)} title="Guest progress is deleted after 7 days without playing"
                    className="shrink-0 flex items-center gap-1 px-2 py-1.5 rounded-lg border-2 border-[#FFE600] bg-[#FFE600]/10 text-[#FFE600] font-heading text-[10px] md:text-xs uppercase tracking-wider animate-pulse hover:bg-[#FFE600]/25">
              <AlertTriangle size={14} /> <span className="hidden sm:inline">Register To Save Progress</span><span className="sm:hidden">Save</span>
            </button>
          )}
          {saveOpen && createPortal(
            <EmailAuthModal upgrade onClose={() => setSaveOpen(false)}
                            onDone={async () => { setSaveOpen(false); await refresh(); toast.success("Progress saved! You can now log in any time."); }} />,
            document.body
          )}
          <CurrencyBadge icon={ASSETS.currency.hotdog} popOnGain label="Hot Dogs" value={user.hot_dogs} color="#FFE600" testId="currency-hotdogs" />
          <CurrencyBadge icon={<HandshakeIcon className="w-[22px] h-[22px]" testId="handshake-icon" />} popOnGain label="Handshakes" value={user.handshakes} color="#00F0FF" testId="currency-handshakes" />
          {/*<CurrencyBadge icon={ASSETS.currency.fan} label="Fans" value={user.fans} color="#FF007F" testId="currency-fans" />*/}

          <button
            data-testid="nav-inbox"
            onClick={() => nav("/inbox")}
            title="Inbox / Messages"
            aria-label="Inbox"
            className={`relative shrink-0 p-2 rounded-lg border transition-all flex items-center gap-1 ${
              loc.pathname.startsWith("/inbox")
                ? "bg-[#FFE600]/15 border-[#FFE600] text-[#FFE600]"
                : "border-[#FFE600]/40 text-[#FFE600] hover:bg-[#FFE600]/10 hover:border-[#FFE600]"
            }`}
          >
            <Mail size={18} />
            {unread > 0 && (
              <span
                data-testid="inbox-unread-badge"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-heading font-bold text-white flex items-center justify-center"
                style={{ background: "#FF007F", boxShadow: "0 0 8px #FF007F99" }}
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>

          <div data-testid="account-name" className="min-w-0 px-2 md:px-3 py-1.5 rounded-lg border border-[#00F0FF]/40 font-heading text-sm max-md:truncate max-[359px]:hidden">
            <span className="neon-cyan">{user.account_name}</span>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="hidden md:block p-2 rounded-lg text-[#A89BCC] hover:text-[#FF007F] hover:bg-[#FF007F]/10 transition-all"
            aria-label="Logout"
          >
            <LogOut size={18} />
          </button>
        </div>
        </div>
      </div>
    </header>
  );
}
